import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const fmt  = n => Number(n||0).toLocaleString('en-US',{maximumFractionDigits:0})
const fmtM = n => { const v=Math.abs(Number(n||0)); if(v>=1e9)return(Number(n)/1e9).toFixed(2)+'B'; if(v>=1e6)return(Number(n)/1e6).toFixed(2)+'M'; if(v>=1e3)return(Number(n)/1e3).toFixed(1)+'K'; return fmt(n) }

const ROLES = [
  { key:'executive',    label:'Executive',          icon:'👔', color:'#1A7A4A' },
  { key:'commercial',   label:'Commercial Manager',  icon:'📋', color:'#2563EB' },
  { key:'cost',         label:'Cost Controller',     icon:'📊', color:'#D97706' },
  { key:'procurement',  label:'Procurement Manager', icon:'🛒', color:'#7C3AED' },
  { key:'finance',      label:'Finance Manager',     icon:'💰', color:'#DC2626' },
]

function KPICard({ label, value, sub, color='#0F1017', borderColor }) {
  return (
    <div style={{ background:'#fff', border:`1px solid #E5E7EB`, borderRadius:10, padding:'14px 18px', borderTop: borderColor ? `3px solid ${borderColor}` : undefined }}>
      <p style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', color:'#6B7280', marginBottom:6 }}>{label}</p>
      <p style={{ fontSize:20, fontWeight:800, color, lineHeight:1.1, marginBottom:4 }}>{value}</p>
      {sub && <p style={{ fontSize:11, color:'#9CA3AF' }}>{sub}</p>}
    </div>
  )
}

function ActionItem({ icon, title, sub, badge, badgeColor='#DC2626', onClick }) {
  return (
    <div onClick={onClick}
      style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', borderRadius:8, border:'1px solid #E5E7EB', background:'#fff', cursor: onClick?'pointer':'default', transition:'all .12s' }}
      onMouseEnter={e => onClick && (e.currentTarget.style.borderColor='#1A7A4A')}
      onMouseLeave={e => onClick && (e.currentTarget.style.borderColor='#E5E7EB')}>
      <span style={{ fontSize:18 }}>{icon}</span>
      <div style={{ flex:1 }}>
        <p style={{ fontSize:12.5, fontWeight:600, color:'#0F1017' }}>{title}</p>
        {sub && <p style={{ fontSize:11, color:'#6B7280', marginTop:1 }}>{sub}</p>}
      </div>
      {badge != null && badge > 0 && (
        <span style={{ background:badgeColor, color:'#fff', borderRadius:10, padding:'2px 8px', fontSize:10, fontWeight:700 }}>{badge}</span>
      )}
      {onClick && <span style={{ color:'#9CA3AF', fontSize:12 }}>›</span>}
    </div>
  )
}

export default function HomeWorkspace({ onNavigate, selectedProject, onSelectProject }) {
  const { profile } = useAuth()
  const [role, setRole] = useState('executive')
  const [projects, setProjects] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(false)
  const now = new Date().toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long',year:'numeric'})
  const name = profile?.full_name?.split(' ')[0] || 'there'

  useEffect(() => { fetchProjects() }, [])
  useEffect(() => { if (selectedProject?.id) fetchStats(selectedProject.id) }, [selectedProject])

  async function fetchProjects() {
    const { data } = await supabase.from('projects')
      .select('id,name,code,contract_value,currency,status,organizations(name)')
      .order('name')
    setProjects(data || [])
    // Normal customer with exactly one authorized project: enter it
    // automatically via the exact same onSelectProject(project) call the
    // manual project-card click already uses (see the button below) --
    // same shape, same function, no separate selection path. Platform
    // admins keep today's behavior (project selector shown) unchanged.
    if (profile?.role !== 'admin' && !selectedProject && (data || []).length === 1) {
      onSelectProject(data[0])
    }
  }

  async function fetchStats(pid) {
    setLoading(true)
    try {
      const [posRes, ipcsRes, prsRes, alertsRes] = await Promise.all([
        supabase.from('purchase_orders').select('id',{count:'exact',head:true}).eq('project_id',pid).eq('status','draft'),
        supabase.from('ipc_certificates').select('id',{count:'exact',head:true}).eq('project_id',pid).in('status',['submitted','under_review']),
        supabase.from('purchase_requisitions').select('id',{count:'exact',head:true}).eq('project_id',pid).eq('status','pending'),
        supabase.from('purchase_orders').select('id',{count:'exact',head:true}).eq('project_id',pid).in('status',['approved','closed']),
      ])
      // Budget data
      const { data: budgetRows } = await supabase.from('budget_summary')
        .select('total_budget,row_type,package_name').eq('project_id',pid)
      const { data: eacRows } = await supabase.from('forecast_eac')
        .select('eac_amount').eq('project_id',pid)
      const totalBudget = (budgetRows||[]).filter(r=>(r.row_type||'').toLowerCase()==='total').reduce((s,r)=>s+(parseFloat(r.total_budget)||0),0)
      const totalEAC    = (eacRows||[]).reduce((s,r)=>s+(parseFloat(r.eac_amount)||0),0)
      const cv = parseFloat(selectedProject?.contract_value||0)
      setStats({
        pendingPOs: posRes.count||0,
        pendingIPCs: ipcsRes.count||0,
        pendingPRs: prsRes.count||0,
        approvedPOs: alertsRes.count||0,
        totalBudget, totalEAC, cv,
        vac: totalBudget - totalEAC,
        gp: cv - totalEAC,
        gpPct: cv > 0 ? ((cv-totalEAC)/cv)*100 : 0,
      })
    } catch(e) { console.error(e) }
    setLoading(false)
  }

  const roleConfig = {
    executive: {
      kpis: stats ? [
        { label:'Contract Value', value: fmtM(stats.cv),        sub: selectedProject?.currency||'SAR', color:'#0F1017', border:'#6B7280' },
        { label:'Total Budget',   value: fmtM(stats.totalBudget),sub: 'Approved budget',               color:'#0F1017', border:'#9CA3AF' },
        { label:'EAC Forecast',   value: fmtM(stats.totalEAC),  sub: stats.totalEAC>stats.totalBudget?'▲ Over budget':'✓ Within budget', color:stats.totalEAC>stats.totalBudget?'#DC2626':'#1A7A4A', border:stats.totalEAC>stats.totalBudget?'#DC2626':'#1A7A4A' },
        { label:'Gross Profit',   value: (stats.gpPct||0).toFixed(1)+'%', sub: fmtM(stats.gp)+' '+( selectedProject?.currency||'SAR'), color:stats.gpPct>=0?'#1A7A4A':'#DC2626', border:stats.gpPct>=0?'#1A7A4A':'#DC2626' },
      ] : [],
      actions: [
        { icon:'📊', title:'Cost Control Report',    sub:'Monthly actuals vs budget',    onClick:()=>onNavigate('costcontrol') },
        { icon:'📈', title:'Dashboard Overview',      sub:'KPIs and performance metrics', onClick:()=>onNavigate('dashboard') },
        { icon:'⚠️', title:'Alerts & Variances',      sub:'Review critical packages',    onClick:()=>onNavigate('alerts'), badge:stats?.pendingIPCs, badgeColor:'#DC2626' },
        { icon:'📁', title:'Document Center',         sub:'All project files',            onClick:()=>onNavigate('documents') },
        { icon:'📉', title:'Project Curves',          sub:'S-Curve & earned value',       onClick:()=>onNavigate('curves') },
        { icon:'🔬', title:'Rate Analysis',           sub:'Budget vs actual rates',       onClick:()=>onNavigate('rate-analysis') },
      ],
    },
    commercial: {
      kpis: stats ? [
        { label:'Pending IPCs',   value: stats.pendingIPCs,  sub:'Awaiting review',    color:stats.pendingIPCs>0?'#D97706':'#1A7A4A', border:stats.pendingIPCs>0?'#D97706':'#1A7A4A' },
        { label:'Active POs',     value: stats.approvedPOs,  sub:'Approved & in progress', color:'#0F1017', border:'#6B7280' },
        { label:'Gross Profit',   value: (stats.gpPct||0).toFixed(1)+'%', sub: fmtM(stats.gp), color:stats.gpPct>=0?'#1A7A4A':'#DC2626', border:stats.gpPct>=0?'#1A7A4A':'#DC2626' },
        { label:'VAC',            value: fmtM(stats.vac),    sub:stats.vac>=0?'Under budget':'Over budget', color:stats.vac>=0?'#1A7A4A':'#DC2626', border:stats.vac>=0?'#1A7A4A':'#DC2626' },
      ] : [],
      actions: [
        { icon:'📄', title:'IPC Certificates',   sub:`${stats?.pendingIPCs||0} pending approval`,   onClick:()=>onNavigate('ipc'),         badge:stats?.pendingIPCs, badgeColor:'#D97706' },
        { icon:'📝', title:'Subcontracts',        sub:'Contract management',                          onClick:()=>onNavigate('subcontracts') },
        { icon:'📋', title:'Final Account',       sub:'Contract closeout',                            onClick:()=>onNavigate('final-account') },
        { icon:'🔄', title:'Change Register',     sub:'Variation orders & TCs',                      onClick:()=>onNavigate('change-register') },
        { icon:'⚙️', title:'Risks & Changes',      sub:'Engineering change tracker',                  onClick:()=>onNavigate('risks-changes') },
        { icon:'📁', title:'Document Center',     sub:'All project files',                           onClick:()=>onNavigate('documents') },
      ],
    },
    cost: {
      kpis: stats ? [
        { label:'EAC vs Budget',  value: stats.totalBudget>0?((stats.totalEAC/stats.totalBudget)*100).toFixed(1)+'%':'—', sub:'EAC as % of budget', color:stats.totalEAC>stats.totalBudget?'#DC2626':'#1A7A4A', border:stats.totalEAC>stats.totalBudget?'#DC2626':'#1A7A4A' },
        { label:'Total EAC',      value: fmtM(stats.totalEAC),   sub:'Forecast at completion',  color:'#0F1017', border:'#6B7280' },
        { label:'VAC',            value: fmtM(stats.vac),        sub:stats.vac>=0?'Saving':'Overrun', color:stats.vac>=0?'#1A7A4A':'#DC2626', border:stats.vac>=0?'#1A7A4A':'#DC2626' },
        { label:'Total Budget',   value: fmtM(stats.totalBudget),sub:'BAC',                      color:'#0F1017', border:'#9CA3AF' },
      ] : [],
      actions: [
        { icon:'📊', title:'Cost Control',         sub:'Monthly control report',          onClick:()=>onNavigate('costcontrol') },
        { icon:'🔮', title:'Direct Forecast',       sub:'EAC — direct works',             onClick:()=>onNavigate('direct-forecast') },
        { icon:'🔮', title:'Indirect Forecast',     sub:'EAC — indirect works',           onClick:()=>onNavigate('indirect-forecast') },
        { icon:'📝', title:'Site Allocation Draft', sub:'Code labor/equipment site entries', onClick:()=>onNavigate('site-allocation') },
        { icon:'📐', title:'QS Quantity Tracking',   sub:'Monthly FQAC & progress',        onClick:()=>onNavigate('qs-tracking') },
        { icon:'⚠️', title:'Alerts & Variances',   sub:'Packages over budget',            onClick:()=>onNavigate('alerts') },
        { icon:'📁', title:'Document Center',       sub:'All project files',               onClick:()=>onNavigate('documents') },
      ],
    },
    procurement: {
      kpis: stats ? [
        { label:'Pending POs',    value: stats.pendingPOs,  sub:'Awaiting approval',   color:stats.pendingPOs>0?'#D97706':'#1A7A4A', border:stats.pendingPOs>0?'#D97706':'#1A7A4A' },
        { label:'Pending PRs',    value: stats.pendingPRs,  sub:'To be actioned',       color:stats.pendingPRs>0?'#D97706':'#1A7A4A', border:stats.pendingPRs>0?'#D97706':'#1A7A4A' },
        { label:'Approved POs',   value: stats.approvedPOs, sub:'Active orders',        color:'#1A7A4A', border:'#1A7A4A' },
        { label:'Contract Value', value: fmtM(stats.cv),    sub:selectedProject?.currency||'SAR', color:'#0F1017', border:'#9CA3AF' },
      ] : [],
      actions: [
        { icon:'🛒', title:'Purchase Orders',   sub:`${stats?.pendingPOs||0} pending`,   onClick:()=>onNavigate('purchase-orders'),   badge:stats?.pendingPOs, badgeColor:'#D97706' },
        { icon:'📋', title:'Requisitions',      sub:`${stats?.pendingPRs||0} to action`, onClick:()=>onNavigate('purchase-requisitions'), badge:stats?.pendingPRs, badgeColor:'#DC2626' },
        { icon:'📦', title:'Store',             sub:'Material inventory',                onClick:()=>onNavigate('store') },
        { icon:'🚜', title:'Equipment Productivity', sub:'Equipment output tracking',    onClick:()=>onNavigate('equipment-productivity') },
      ],
    },
    finance: {
      kpis: stats ? [
        { label:'Gross Profit',   value:(stats.gpPct||0).toFixed(1)+'%', sub:fmtM(stats.gp)+' '+(selectedProject?.currency||'SAR'), color:stats.gpPct>=0?'#1A7A4A':'#DC2626', border:stats.gpPct>=0?'#1A7A4A':'#DC2626' },
        { label:'Contract Value', value:fmtM(stats.cv),    sub:selectedProject?.currency||'SAR', color:'#0F1017', border:'#9CA3AF' },
        { label:'EAC Forecast',   value:fmtM(stats.totalEAC), sub:'Cost at completion', color:'#0F1017', border:'#6B7280' },
        { label:'VAC',            value:fmtM(stats.vac),    sub:stats.vac>=0?'Saving':'Overrun', color:stats.vac>=0?'#1A7A4A':'#DC2626', border:stats.vac>=0?'#1A7A4A':'#DC2626' },
      ] : [],
      actions: [
        { icon:'💸', title:'Finance Transactions', sub:'Cash in / out',              onClick:()=>onNavigate('finance') },
        { icon:'💵', title:'Petty Cash',           sub:'Voucher management',          onClick:()=>onNavigate('petty-cash') },
        { icon:'📅', title:'Accrual',              sub:'Pending cost provisions',     onClick:()=>onNavigate('accrual') },
        { icon:'📊', title:'Cost Control',         sub:'Actuals vs budget',           onClick:()=>onNavigate('costcontrol') },
        { icon:'📁', title:'Document Center',       sub:'All project files',               onClick:()=>onNavigate('documents') },
      ],
    },
  }

  const cfg = roleConfig[role]

  return (
    <div style={{ minHeight:'100vh', background:'#F3F4F6', fontFamily:'inherit' }}>

      {/* ── Header ── */}
      <div style={{ background:'#fff', borderBottom:'1px solid #E5E7EB', padding:'20px 24px 16px' }}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16 }}>
          <div>
            <p style={{ fontSize:11, fontWeight:600, color:'#9CA3AF', textTransform:'uppercase', letterSpacing:'.1em', marginBottom:4 }}>{now}</p>
            <h1 style={{ fontSize:22, fontWeight:800, color:'#0F1017', letterSpacing:'-.02em', marginBottom:3 }}>
              Good {new Date().getHours()<12?'morning':new Date().getHours()<17?'afternoon':'evening'}, {name}
            </h1>
            <p style={{ fontSize:12, color:'#6B7280' }}>
              {selectedProject ? `Viewing: ${selectedProject.name}` : 'Select a project to see your workspace'}
            </p>
          </div>
          {/* Role switcher */}
          <div style={{ display:'flex', gap:4, flexWrap:'wrap', justifyContent:'flex-end' }}>
            {ROLES.map(r => (
              <button key={r.key} onClick={() => setRole(r.key)}
                style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'5px 12px', borderRadius:20, fontSize:11, fontWeight:600, cursor:'pointer', fontFamily:'inherit', transition:'all .12s',
                  background: role===r.key ? r.color : '#fff',
                  color:       role===r.key ? '#fff'    : '#6B7280',
                  border:      role===r.key ? `1px solid ${r.color}` : '1px solid #E5E7EB',
                }}>
                <span>{r.icon}</span> {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ padding:'20px 24px', display:'flex', flexDirection:'column', gap:16 }}>

        {/* ── Project Selector ── */}
        {!selectedProject && projects.length === 0 && (
          <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:10, padding:16 }}>
            <p style={{ fontSize:13, color:'#6B7280' }}>No project assigned. Contact your administrator for access.</p>
          </div>
        )}

        {!selectedProject && projects.length > 0 && (
          <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:10, padding:16 }}>
            <p style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.1em', color:'#6B7280', marginBottom:12 }}>Select Project</p>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:10 }}>
              {projects.map(p => (
                <button key={p.id} onClick={() => onSelectProject(p)}
                  style={{ textAlign:'left', padding:'12px 14px', borderRadius:8, border:'1px solid #E5E7EB', background:'#fff', cursor:'pointer', fontFamily:'inherit', transition:'all .12s' }}
                  onMouseEnter={e=>{ e.currentTarget.style.borderColor='#1A7A4A'; e.currentTarget.style.boxShadow='0 2px 8px rgba(26,122,74,.1)' }}
                  onMouseLeave={e=>{ e.currentTarget.style.borderColor='#E5E7EB'; e.currentTarget.style.boxShadow='none' }}>
                  <p style={{ fontWeight:700, color:'#0F1017', fontSize:13 }}>{p.name}</p>
                  <p style={{ fontSize:11, color:'#9CA3AF', marginTop:2 }}>{p.organizations?.name}</p>
                  <p style={{ fontSize:11, color:'#6B7280', marginTop:6, fontWeight:600 }}>{fmt(p.contract_value)} {p.currency}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {selectedProject && (
          <>
            {/* ── KPIs ── */}
            {loading ? (
              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'20px 0', color:'#9CA3AF' }}>
                <div style={{ width:18, height:18, border:'2px solid #E5E7EB', borderTopColor:'#1A7A4A', borderRadius:'50%', animation:'spin 1s linear infinite' }}/>
                Loading workspace…
              </div>
            ) : cfg.kpis.length > 0 && (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
                {cfg.kpis.map((k,i) => (
                  <KPICard key={i} label={k.label} value={k.value} sub={k.sub} color={k.color} borderColor={k.border}/>
                ))}
              </div>
            )}

            {/* ── Actions Today + Quick Access ── */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
              {/* Actions Today */}
              <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:10, padding:'18px 20px' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
                  <p style={{ fontSize:13, fontWeight:700, color:'#0F1017' }}>Actions Required Today</p>
                  <span style={{ fontSize:10, color:'#9CA3AF' }}>{new Date().toLocaleDateString('en-GB',{day:'numeric',month:'short'})}</span>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {stats && (stats.pendingPOs > 0 || stats.pendingIPCs > 0 || stats.pendingPRs > 0) ? (
                    <>
                      {stats.pendingPOs > 0 && (
                        <ActionItem icon="🛒" title="Purchase Orders pending approval" sub={`${stats.pendingPOs} POs awaiting sign-off`} badge={stats.pendingPOs} badgeColor="#D97706" onClick={() => onNavigate('purchase-orders')}/>
                      )}
                      {stats.pendingIPCs > 0 && (
                        <ActionItem icon="📄" title="IPC Certificates to review" sub={`${stats.pendingIPCs} IPCs submitted`} badge={stats.pendingIPCs} badgeColor="#DC2626" onClick={() => onNavigate('ipc')}/>
                      )}
                      {stats.pendingPRs > 0 && (
                        <ActionItem icon="📋" title="Purchase Requisitions pending" sub={`${stats.pendingPRs} PRs to action`} badge={stats.pendingPRs} badgeColor="#7C3AED" onClick={() => onNavigate('purchase-requisitions')}/>
                      )}
                    </>
                  ) : (
                    <div style={{ padding:'24px 0', textAlign:'center', color:'#9CA3AF' }}>
                      <p style={{ fontSize:22, marginBottom:8 }}>✅</p>
                      <p style={{ fontSize:12, fontWeight:600 }}>All clear — no pending actions</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Access */}
              <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:10, padding:'18px 20px' }}>
                <p style={{ fontSize:13, fontWeight:700, color:'#0F1017', marginBottom:14 }}>Quick Access</p>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {cfg.actions.map((a,i) => (
                    <ActionItem key={i} {...a}/>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Admin Quick Access ── */}
            <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:10, padding:'18px 20px' }}>
              <p style={{ fontSize:13, fontWeight:700, color:'#0F1017', marginBottom:14 }}>Admin</p>
              <div style={{ display:'flex', gap:10 }}>
                <button onClick={() => onNavigate('users')}
                  style={{ flex:1, padding:'12px 14px', borderRadius:8, border:'1px solid #E5E7EB', background:'#fff', cursor:'pointer', textAlign:'left', fontFamily:'inherit' }}>
                  <p style={{ fontSize:13 }}>👥</p>
                  <p style={{ fontSize:12, fontWeight:700, color:'#0F1017', marginTop:4 }}>Users & Roles</p>
                  <p style={{ fontSize:11, color:'#9CA3AF', marginTop:2 }}>Manage team members</p>
                </button>
                <button onClick={() => onNavigate('permissions')}
                  style={{ flex:1, padding:'12px 14px', borderRadius:8, border:'1px solid #E5E7EB', background:'#fff', cursor:'pointer', textAlign:'left', fontFamily:'inherit' }}>
                  <p style={{ fontSize:13 }}>🔐</p>
                  <p style={{ fontSize:12, fontWeight:700, color:'#0F1017', marginTop:4 }}>Permissions</p>
                  <p style={{ fontSize:11, color:'#9CA3AF', marginTop:2 }}>Role access control</p>
                </button>
                <button onClick={() => onNavigate('cost-codes')}
                  style={{ flex:1, padding:'12px 14px', borderRadius:8, border:'1px solid #E5E7EB', background:'#fff', cursor:'pointer', textAlign:'left', fontFamily:'inherit' }}>
                  <p style={{ fontSize:13 }}>🏗️</p>
                  <p style={{ fontSize:12, fontWeight:700, color:'#0F1017', marginTop:4 }}>Cost Codes</p>
                  <p style={{ fontSize:11, color:'#9CA3AF', marginTop:2 }}>Manage cost structure</p>
                </button>
              </div>
            </div>

            {/* ── Portfolio: All Projects ── */}
            <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:10, padding:'18px 20px' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
                <p style={{ fontSize:13, fontWeight:700, color:'#0F1017' }}>Portfolio — All Projects</p>
                <button onClick={() => onNavigate('projects')}
                  style={{ fontSize:11, color:'#1A7A4A', background:'none', border:'1px solid rgba(26,122,74,.25)', borderRadius:6, padding:'3px 10px', cursor:'pointer', fontFamily:'inherit' }}>
                  Manage →
                </button>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:10 }}>
                {projects.map(p => (
                  <button key={p.id} onClick={() => onSelectProject(p)}
                    style={{ textAlign:'left', padding:'12px 14px', borderRadius:8, border:`1px solid ${selectedProject?.id===p.id?'#1A7A4A':'#E5E7EB'}`, background:selectedProject?.id===p.id?'rgba(26,122,74,.05)':'#fff', cursor:'pointer', fontFamily:'inherit', transition:'all .12s' }}
                    onMouseEnter={e=>e.currentTarget.style.borderColor='#1A7A4A'}
                    onMouseLeave={e=>{ if(selectedProject?.id!==p.id) e.currentTarget.style.borderColor='#E5E7EB' }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                      <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:10, background:p.status==='active'?'#DCFCE7':'#F3F4F6', color:p.status==='active'?'#14532D':'#6B7280', border:`1px solid ${p.status==='active'?'#BBF7D0':'#E5E7EB'}` }}>{p.status}</span>
                      {selectedProject?.id===p.id && <span style={{ fontSize:9, color:'#1A7A4A', fontWeight:700 }}>ACTIVE</span>}
                    </div>
                    <p style={{ fontWeight:700, color:'#0F1017', fontSize:12.5, marginBottom:2 }}>{p.name}</p>
                    <p style={{ fontSize:10.5, color:'#9CA3AF' }}>{p.organizations?.name}</p>
                    <p style={{ fontSize:12, color:'#0F1017', fontWeight:600, marginTop:6 }}>{fmtM(p.contract_value)} {p.currency}</p>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}
